import { Variant } from "@mui/material/styles/createTypography";
import Typography from "@mui/material/Typography";
import React from "react";

export default function CenteredTextBox({
  text = "",
  useMaxSpace = false,
  maxHeight = 300, // px
  lineHeight = 20, // px (used to calculate how many lines fit in maxHeight)
  className = "",
  style = {},
  variant = "body1",
}: {
  text: string;
  useMaxSpace: boolean;
  maxHeight?: number | string;
  lineHeight?: number;
  className?: string;
  style?: React.CSSProperties;
  variant?: Variant;
}) {
  // Calculate how many lines fit into the box and set the -webkit-line-clamp accordingly
  const maxLines = Math.max(
    1,
    maxHeight === "auto"
      ? 10000
      : Math.floor((maxHeight as number) / lineHeight)
  );

  // Split the incoming text on <br> and <br/> (case-insensitive) so those become real breaks.
  // We preserve other HTML characters (we DON'T use dangerouslySetInnerHTML).
  const parts =
    typeof text === "string" ? text.split(/<br\s*\/?>/gi) : [String(text)];

  // Inline styles required for the multiline ellipsis and centering.
  // Note: -webkit-line-clamp only works with display: -webkit-box and WebkitBoxOrient vertical.
  const boxStyles = {
    maxHeight: useMaxSpace ? "100%" : `${maxHeight}px`,
    lineHeight: `${lineHeight}px`,
    display: "-webkit-box",
    WebkitBoxOrient: "vertical" as const,
    WebkitLineClamp: maxLines as unknown as string, // TS quirk
    overflow: "hidden",
    //textAlign: 'center',
    // For accessibility and consistent wrapping, allow normal wrapping.
    // wordBreak: 'break-word',
    // Allow the element to center itself and not stretch
    margin: "0 auto",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...style,
  };

  return (
    <Typography
      className={`centered-text-box ${className}`}
      style={boxStyles}
      aria-label="Centered text box"
      sx={{ textAlign: "left" }}
      variant={variant}
    >
      {/* Render parts and insert real <br/> elements where original <br> markers were. */}
      {parts.map((part, i) => (
        <React.Fragment key={i}>
          {/* Preserve whitespace inside each part (so multiple spaces are shown) */}
          <span style={{ whiteSpace: "pre-wrap" }}>{part}</span>
          {i < parts.length - 1 && <br />}
        </React.Fragment>
      ))}
    </Typography>
  );
}

/**
 * Example usage:
 *
 * <CenteredTextBox
 * text={"This is a centered line.<br>This is a second line that could be long and will be clamped."}
 * maxHeight={300}
 * lineHeight={22}
 * />
 *
 * If you change font-size in CSS, adjust `lineHeight` to match the actual rendered line height
 * for the best ellipsis behavior.
 */
